import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { SUBJECTS } from "@/lib/constants";
import { Play, Pause, RotateCcw, Timer } from "lucide-react";
import { toast } from "sonner";

const FOCUS_MIN = 25;
const BREAK_MIN = 5;

export default function Pomodoro() {
  const [phase, setPhase] = useState("focus");
  const [secondsLeft, setSecondsLeft] = useState(FOCUS_MIN * 60);
  const [running, setRunning] = useState(false);
  const [subject, setSubject] = useState("Mathématiques");
  const [cycles, setCycles] = useState(0);
  const totalRef = useRef(FOCUS_MIN * 60);

  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => clearInterval(t);
  }, [running]);

  useEffect(() => {
    if (secondsLeft > 0) return;
    if (phase === "focus") {
      api.post("/activities", { minutes: FOCUS_MIN, subject })
        .then(() => toast.success(`Session de ${FOCUS_MIN} min enregistrée dans ta progression ! Pause bien méritée.`))
        .catch(() => {});
      setPhase("break");
      setSecondsLeft(BREAK_MIN * 60);
      totalRef.current = BREAK_MIN * 60;
    } else {
      setPhase("focus");
      setSecondsLeft(FOCUS_MIN * 60);
      totalRef.current = FOCUS_MIN * 60;
      setCycles((c) => c + 1);
      toast.info("Pause terminée, on repart pour 25 min !");
    }
  }, [secondsLeft]);

  const reset = () => {
    setRunning(false);
    setPhase("focus");
    setSecondsLeft(FOCUS_MIN * 60);
    totalRef.current = FOCUS_MIN * 60;
  };

  const mm = String(Math.floor(secondsLeft / 60)).padStart(2, "0");
  const ss = String(secondsLeft % 60).padStart(2, "0");
  const pct = 100 - Math.round((secondsLeft / totalRef.current) * 100);
  const isFocus = phase === "focus";

  return (
    <div
      className={`rounded-3xl border p-6 shadow-sm transition-colors duration-500 ${
        isFocus
          ? "bg-gradient-to-br from-indigo-600 to-violet-600 border-indigo-500"
          : "bg-gradient-to-br from-emerald-500 to-teal-600 border-emerald-400"
      }`}
      data-testid="pomodoro-widget"
    >
      <div className="flex flex-wrap items-center gap-6">
        <div className="flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-white/15 flex items-center justify-center">
            <Timer className="h-7 w-7 text-white" />
          </div>
          <div>
            <p className="text-white/80 text-xs font-bold uppercase tracking-wider">
              {isFocus ? "Session focus" : "Pause"}
            </p>
            <p className="font-display text-4xl font-bold text-white tabular-nums" data-testid="pomodoro-timer">
              {mm}:{ss}
            </p>
          </div>
        </div>
        <div className="flex-1 min-w-[180px]">
          <div className="h-2.5 rounded-full bg-white/20 overflow-hidden mb-3">
            <div className="h-full bg-white/90 rounded-full transition-[width] duration-1000" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={() => setRunning(!running)}
              data-testid="pomodoro-toggle"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-indigo-700 text-sm font-bold hover:bg-indigo-50 transition-colors duration-200"
            >
              {running ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
              {running ? "Pause" : secondsLeft < totalRef.current ? "Reprendre" : "Démarrer"}
            </button>
            <button
              onClick={reset}
              data-testid="pomodoro-reset"
              className="p-2.5 rounded-full bg-white/15 text-white hover:bg-white/25 transition-colors duration-200"
            >
              <RotateCcw className="h-4 w-4" />
            </button>
            {isFocus && (
              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="pomodoro-subject"
                className="px-4 py-2.5 rounded-full bg-white/15 text-white text-sm font-semibold border-0 focus:ring-2 focus:ring-white/40 [&>option]:text-slate-900"
              >
                {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            )}
          </div>
        </div>
        <div className="text-right hidden sm:block">
          <p className="text-white/80 text-xs font-bold uppercase tracking-wider">Aujourd'hui</p>
          <p className="text-white text-sm font-semibold" data-testid="pomodoro-cycles">{cycles} cycle{cycles > 1 ? "s" : ""} terminé{cycles > 1 ? "s" : ""}</p>
          <p className="text-white/70 text-xs mt-0.5">25 min focus · 5 min pause</p>
        </div>
      </div>
    </div>
  );
}
